import React, { useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tag, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const SWATCHES = ["#ef4444", "#f59e0b", "#eab308", "#84cc16", "#10b981", "#14b8a6",
  "#0ea5e9", "#6366f1", "#8b5cf6", "#ec4899", "#f43f5e", "#78716c"];

const slug = (s) => s.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export default function ManageTypesDialog({ open, onOpenChange, customTypes, onSave }) {
  const [form, setForm] = useState({ label: "", color: SWATCHES[6], is_owner: false, is_kit: false });
  const list = customTypes || [];

  const add = async () => {
    const label = form.label.trim();
    if (!label) return;
    const key = "c_" + slug(label);
    if (list.some((t) => t.key === key)) { toast.error("A type with that name already exists"); return; }
    await onSave([...list, { key, label, color: form.color, is_owner: form.is_owner, is_kit: form.is_kit }]);
    setForm({ label: "", color: SWATCHES[6], is_owner: false, is_kit: false });
    toast.success("Type added");
  };

  const remove = async (key) => {
    await onSave(list.filter((t) => t.key !== key));
    toast.success("Type removed");
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover" data-testid="manage-types-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl flex items-center gap-2"><Tag className="w-5 h-5 text-primary" /> Custom types</DialogTitle>
          <DialogDescription>
            Create your own codex detail types. Mark a type as an owner to give it loadouts (like characters/vehicles).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          {list.length > 0 && (
            <div className="space-y-1.5">
              {list.map((t) => (
                <div key={t.key} data-testid={`custom-type-${t.key}`} className="flex items-center gap-2.5 rounded-lg border border-border p-2.5">
                  <span className="w-4 h-4 rounded-full shrink-0" style={{ background: t.color }} />
                  <span className="flex-1 text-sm font-medium">{t.label}</span>
                  {t.is_owner && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-primary/15 text-primary">owner</span>}
                  {t.is_kit && <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-500">kit</span>}
                  <button onClick={() => remove(t.key)} data-testid={`delete-type-${t.key}`} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
          )}

          <div className="rounded-lg border border-border p-3 space-y-3">
            <div className="space-y-1.5">
              <Label>New type name</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Spell, Creature, Safehouse" data-testid="new-type-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Colour</Label>
              <div className="flex flex-wrap gap-1.5">
                {SWATCHES.map((c) => (
                  <button key={c} onClick={() => setForm({ ...form, color: c })} data-testid={`type-color-${c}`}
                    className={`w-6 h-6 rounded-full transition-transform ${form.color === c ? "ring-2 ring-offset-2 ring-offset-popover ring-foreground scale-110" : ""}`}
                    style={{ background: c }} />
                ))}
              </div>
            </div>
            <div className="flex items-center justify-between rounded-lg bg-secondary/40 p-2.5">
              <div>
                <Label className="cursor-pointer">Can hold a loadout</Label>
                <p className="text-xs text-muted-foreground">Show this type as an owner in Loadouts.</p>
              </div>
              <Switch checked={form.is_owner} onCheckedChange={(v) => setForm({ ...form, is_owner: v })} data-testid="new-type-owner" />
            </div>
            <div className="flex items-center justify-between rounded-lg bg-secondary/40 p-2.5">
              <div>
                <Label className="cursor-pointer">Is a kit / container</Label>
                <p className="text-xs text-muted-foreground">Entries hold a contents list and drop into loadouts as one item.</p>
              </div>
              <Switch checked={form.is_kit} onCheckedChange={(v) => setForm({ ...form, is_kit: v })} data-testid="new-type-kit" />
            </div>
            <Button onClick={add} disabled={!form.label.trim()} data-testid="add-type-button" className="w-full gap-2"><Plus className="w-4 h-4" /> Add type</Button>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
